import {createAsyncThunk} from "@reduxjs/toolkit";
import type {Dealership, DealerUser} from "@/types";
import type {RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {
    getAuthCookies,
    keysToCamelCase,
    keysToSnakeCase,
    makeFileName,
    normalizeError,
    prepareImageFile,
    setAuthCookies,
    uploadToR2
} from "@/utils";

type ForgotPasswordPayload = {
    email: string;
    redirectTo?: string;
};

type ResetPasswordPayload = {
    accessToken?: string;
    refreshToken?: string;
};

type UpdatePasswordPayload = {
    password: string;
};

type UpdateDealershipProfilePayload = Partial<Pick<
    Dealership,
    "name" | "email" | "phone" | "description" | "profile" | "banner" | "locations" | "phoneNumber" | "images"
>>;

type UpdateDealerUserProfilePayload = Partial<Pick<
    DealerUser,
    "firstName" | "lastName" | "email" | "profile"
>>;

const DEALER_MEDIA_BUCKET = "dealer-media";

async function uploadDealerImageFromSource(
    source: string | undefined,
    path: string,
    filename: string,
    options: {
        maxSizeMB: number;
        maxWidthOrHeight: number;
    }
) {
    if (!source) return undefined;
    if (!source.startsWith("blob:")) {
        return source;
    }

    const compressedFile = await prepareImageFile(source, filename, options);
    const storagePath = `${path}/${filename}`;
    return uploadToR2(DEALER_MEDIA_BUCKET, storagePath, compressedFile, "image/webp");
}

async function uploadDealerGalleryImages(images: string[], dealerId: string) {
    const uploaded = await Promise.all(
        images.map((image, index) =>
            uploadDealerImageFromSource(
                image,
                `${dealerId}/gallery`,
                makeFileName(`gallery_${index}`, `gallery_${index}.webp`),
                {
                    maxSizeMB: 0.6,
                    maxWidthOrHeight: 1600
                }
            )
        )
    );

    return uploaded.filter((image): image is string => Boolean(image));
}

export const forgotPassword = createAsyncThunk<
    {message: string},
    ForgotPasswordPayload,
    {rejectValue: string}
>(
    "authentication/forgotPassword",
    async ({email, redirectTo}, {rejectWithValue}) => {
        try {
            const destination = redirectTo ?? `${window.location.origin}/reset-password`;
            const {error} = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: destination
            });

            if (error) {
                return rejectWithValue(error.message);
            }

            return {
                message: "Password reset email sent"
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to send password reset email"));
        }
    }
);

export const resetPassword = createAsyncThunk<
    {message: string},
    ResetPasswordPayload | undefined,
    {rejectValue: string}
>(
    "authentication/resetPassword",
    async (payload, {rejectWithValue}) => {
        try {
            const cookieTokens = getAuthCookies();
            const accessToken = payload?.accessToken ?? cookieTokens?.accessToken;
            const refreshToken = payload?.refreshToken ?? cookieTokens?.refreshToken;

            if (!accessToken || !refreshToken) {
                return rejectWithValue("Reset link is invalid or expired");
            }

            const {data, error} = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            });

            if (error) {
                return rejectWithValue(error.message);
            }

            if (data.session) {
                setAuthCookies(data.session);
            }

            return {
                message: "Recovery session restored"
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to restore reset session"));
        }
    }
);

export const updatePassword = createAsyncThunk<
    {message: string},
    UpdatePasswordPayload,
    {rejectValue: string}
>(
    "authentication/updatePassword",
    async ({password}, {rejectWithValue}) => {
        try {
            const {error} = await supabase.auth.updateUser({password});

            if (error) {
                return rejectWithValue(error.message);
            }

            return {
                message: "Password updated"
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update password"));
        }
    }
);

export const updateDealershipProfile = createAsyncThunk<
    Dealership,
    UpdateDealershipProfilePayload,
    {state: RootState; rejectValue: string}
>(
    "authentication/updateDealershipProfile",
    async (payload, {getState, rejectWithValue}) => {
        try {
            const stateDealerId = getState().authentication.user?.id;
            const stateDealerUser = getState().authentication.dealerUser;
            const stateDealerUserId = stateDealerUser?.id;
            const {data, error} = await supabase.auth.getUser();

            if (error) {
                return rejectWithValue(error.message);
            }

            const authUserId = data.user?.id;
            if (!authUserId) {
                return rejectWithValue("Session expired. Please log in again.");
            }

            if (stateDealerUserId && stateDealerUserId !== authUserId) {
                return rejectWithValue("Auth mismatch detected.");
            }
            if (!stateDealerId) {
                return rejectWithValue("No dealership profile found.");
            }
            if (stateDealerUser?.role !== "admin") {
                return rejectWithValue("Only dealer admins can update dealership details.");
            }

            const uploadedProfile = await uploadDealerImageFromSource(
                payload.profile,
                `${stateDealerId}/profile`,
                makeFileName("profile", "profile.webp"),
                {
                    maxSizeMB: 0.08,
                    maxWidthOrHeight: 320
                }
            );

            const uploadedBanner = await uploadDealerImageFromSource(
                payload.banner,
                `${stateDealerId}/banner`,
                makeFileName("banner", "banner.webp"),
                {
                    maxSizeMB: 0.4,
                    maxWidthOrHeight: 1400
                }
            );

            const uploadedImages = payload.images
                ? await uploadDealerGalleryImages(payload.images, stateDealerId)
                : undefined;

            const updatePayload = keysToSnakeCase({
                ...payload,
                profile: uploadedProfile ?? payload.profile,
                banner: uploadedBanner ?? payload.banner,
                images: uploadedImages ?? payload.images,
                phoneNumber: payload.phoneNumber ?? payload.phone
            });

            const {data: updatedProfile, error: updateError} = await supabase
                .from("dealerships")
                .update(updatePayload)
                .eq("id", stateDealerId)
                .select("*")
                .single();

            if (updateError) {
                return rejectWithValue(updateError.message);
            }

            return keysToCamelCase<Dealership>(updatedProfile);
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update dealership"));
        }
    }
);

export const updateDealerUserProfile = createAsyncThunk<
    DealerUser,
    UpdateDealerUserProfilePayload,
    {state: RootState; rejectValue: string}
>(
    "authentication/updateDealerUserProfile",
    async (payload, {getState, rejectWithValue}) => {
        try {
            const stateDealerUserId = getState().authentication.dealerUser?.id;
            const {data, error} = await supabase.auth.getUser();

            if (error) {
                return rejectWithValue(error.message);
            }

            const authUserId = data.user?.id;
            if (!authUserId) {
                return rejectWithValue("Session expired. Please log in again.");
            }

            if (stateDealerUserId && stateDealerUserId !== authUserId) {
                return rejectWithValue("Auth mismatch detected.");
            }

            if (payload.email && payload.email !== data.user?.email) {
                const emailUpdate = await supabase.auth.updateUser({email: payload.email});
                if (emailUpdate.error) {
                    return rejectWithValue(emailUpdate.error.message);
                }
            }

            const {data: updatedDealerUser, error: updateError} = await supabase
                .from("dealer_users")
                .update(keysToSnakeCase(payload))
                .eq("id", authUserId)
                .select("*")
                .single();

            if (updateError) {
                return rejectWithValue(updateError.message);
            }

            return keysToCamelCase<DealerUser>(updatedDealerUser);
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update dealer user"));
        }
    }
);
